import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { ContributionService } from "services/contribution.service";
import { DepartmentService } from "services/department.service";
import { IContributionResponse } from "models/church.types";

export default function ContributionList() {
  const [contributions, setContributions] = useState<IContributionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalGlobalAmount, setTotalGlobalAmount] = useState<number>(0);

  // Référentiel des départements pour le filtre
  const [deptOptions, setDeptOptions] = useState<{ value: string; label: string }[]>([]);

  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const typeIdFromUrl = queryParams.get("typeId");
  const deptIdFromUrl = queryParams.get("deptId");

  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Calcul du total de la page actuelle
  const totalAmount = contributions.reduce(
    (acc, curr) => acc + (curr.amount || 0),
    0
  );

  // 1. Chargement du catalogue des départements
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const depts = await DepartmentService.getAllSimple();
        setDeptOptions(depts.map((d: any) => ({ value: d.id.toString(), label: d.name })));
      } catch (err) {
        console.error("Erreur chargement des départements:", err);
      }
    };
    loadDepartments();
  }, []);

  // 2. Fonction de chargement combinée avec mise à jour du total global selon les filtres
  const loadData = useCallback(
    async (query: string, page: number, typeId: string | null, deptId: string | null) => {
      setLoading(true);
      try {
        let result;
        let summaryAmount = 0;

        // Priorité 1 : Recherche textuelle
        if (query.trim()) {
          result = await ContributionService.search(query, page, itemsPerPage);
          const allSearchResults = await ContributionService.search(query, 0, 10000).catch(() => ({ data: [] }));
          summaryAmount = (allSearchResults.data || []).reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);
        }
        // Priorité 2 : Filtrage par département
        else if (deptId) {
          result = await ContributionService.getByDepartment(deptId, page, itemsPerPage);
          const deptContributions = await ContributionService.getByDepartment(deptId, 0, 10000).catch(() => ({ data: [] }));
          summaryAmount = (deptContributions.data || []).reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);
        }
        // Priorité 3 : Filtrage par type de contribution
        else if (typeId) {
          result = await ContributionService.getByType(typeId, page, itemsPerPage);
          summaryAmount = await ContributionService.getSummary(
            undefined,
            undefined,
            Number(typeId)
          ).catch(() => 0);
        }
        // Cas par défaut : Récupérer toutes les contributions
        else {
          result = await ContributionService.getAll(page, itemsPerPage);
          summaryAmount = await ContributionService.getSummary(
            undefined,
            undefined,
            undefined
          ).catch(() => 0);
        }

        setContributions(result.data || []);
        setTotalItems(result.totalCount || 0);
        setTotalGlobalAmount(summaryAmount || 0);

      } catch (error) {
        console.error("Erreur chargement contributions:", error);
        toast.error("Impossible de charger la liste des contributions");
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage]
  );

  // 3. Debounce pour la recherche
  const debouncedSearch = useCallback(
    debounce((value: string, typeId: string | null, deptId: string | null) => {
      setCurrentPage(0);
      loadData(value, 0, typeId, deptId);
    }, 500),
    [loadData]
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value, typeIdFromUrl, deptIdFromUrl);
  };

  // 4. Gestion du filtre par département
  const handleDeptChange = (selected: any) => {
    const newDeptId = selected ? selected.value : null;
    const params = new URLSearchParams(location.search);
    if (newDeptId) params.set("deptId", newDeptId);
    else params.delete("deptId");
    history.push({ search: params.toString() });
    setCurrentPage(0);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Supprimer cet enregistrement de contribution ?")) {
      setLoading(true);
      try {
        await ContributionService.delete(id);
        toast.success("Contribution supprimée");
        loadData(searchTerm, currentPage, typeIdFromUrl, deptIdFromUrl);
      } catch (error) {
        toast.error("Erreur lors de la suppression.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setCurrentPage(0);
    history.push("/admin/contributions");
  };

  // 5. Effet d'écoute centralisé
  useEffect(() => {
    loadData(searchTerm, currentPage, typeIdFromUrl, deptIdFromUrl);
  }, [currentPage, typeIdFromUrl, deptIdFromUrl, loadData]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const customStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: "#1e293b",
      borderColor: "#334155",
      color: "white",
      padding: "2px",
      minHeight: "38px",
      height: "38px",
      borderRadius: "0.5rem",
      boxShadow: "none",
      fontFamily: "ui-sans-serif, system-ui, sans-serif",
      "&:hover": { borderColor: "#475569" },
    }),
    singleValue: (base: any) => ({ ...base, color: "white" }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1e293b",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#334155" : "transparent",
      color: "white",
      cursor: "pointer",
    }),
  };

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Gestion des Contributions
        </h2>
        <p className="text-slate-400 text-sm italic">
          {deptIdFromUrl
            ? "Filtrage actif par département"
            : typeIdFromUrl
            ? "Filtrage actif par type de cotisation"
            : "Historique des dons, dîmes et offrandes des membres"}
        </p>
      </div>

      {/* BARRE DE FILTRES MULTICRITÈRES */}
      <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex flex-col md:flex-row items-center gap-3 w-full lg:w-auto flex-1">
          {/* Champ de recherche */}
          <div className="relative w-full md:w-80 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher par membre ou type..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Filtre par Département */}
          <div className="w-full md:w-64">
            <Select
              options={deptOptions}
              styles={customStyles}
              placeholder="Tous les départements"
              isClearable
              value={deptOptions.find((o) => o.value === deptIdFromUrl) || null}
              onChange={handleDeptChange}
              menuPortalTarget={document.body}
            />
          </div>

          {(typeIdFromUrl || deptIdFromUrl || searchTerm) && (
            <button
              onClick={handleResetFilters}
              className="bg-slate-700 text-white px-3 h-[38px] rounded-lg hover:text-red-400 border border-slate-600 transition-all flex items-center justify-center gap-2 text-xs font-bold"
              title="Réinitialiser tous les filtres"
            >
              <i className="fas fa-times-circle text-sm"></i> Effacer filtres
            </button>
          )}
        </div>

        <Link
          to="/admin/contributions/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-hand-holding-usd mr-2 text-base"></i> Enregistrer contribution
        </Link>
      </div>

      {/* TABLEAU DES CONTRIBUTIONS */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Membre</th>
                <th className="px-6 py-5">Département</th>
                <th className="px-6 py-5">Type</th>
                <th className="px-6 py-5 text-right">Montant</th>
                <th className="px-6 py-5 text-center">Date de paiement</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400">
                    <i className="fas fa-circle-notch animate-spin mr-2"></i> Chargement des données...
                  </td>
                </tr>
              ) : contributions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-500 italic text-sm">
                    <i className="fas fa-search-dollar block text-3xl mb-3 opacity-20"></i>
                    Aucune contribution trouvée.
                  </td>
                </tr>
              ) : (
                contributions.map((con) => (
                  <tr key={con.id} className="hover:bg-slate-700/40 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700 shadow-inner uppercase">
                          {con.memberFullName?.charAt(0) || "M"}
                        </div>
                        <span className="text-sm font-bold text-white uppercase tracking-tight">
                          {con.memberFullName ?? "Membre inconnu"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {con.departmentName ? (
                        <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-1 rounded text-[10px] font-semibold">
                          {con.departmentName}
                        </span>
                      ) : (
                        <span className="text-slate-500 italic text-[11px]">Aucun</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full bg-slate-900 text-[#D4AF37] text-[10px] font-black uppercase border border-[#D4AF37]/20">
                        {con.typeName ?? "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-black text-white">
                        {(con.amount ?? 0).toLocaleString()} FCFA
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[10px] font-medium text-center uppercase">
                      <span className="text-slate-400">
                        {con.paymentDate ? new Date(con.paymentDate).toLocaleDateString() : "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/contributions/edit/${con.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(con.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {/* Pied de tableau avec total de la page */}
            <tfoot className="bg-[#1E293B]">
              <tr>
                <td
                  colSpan={3}
                  className="px-6 py-4 text-right font-black text-slate-300 uppercase text-[10px]"
                >
                  Total Page :
                </td>
                <td className="px-6 py-4 text-right font-black text-[#D4AF37]">
                  {totalAmount.toLocaleString()} FCFA
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
            Total global : <span className="text-[#D4AF37]">{totalItems}</span> contributions | Montant Global : <span className="text-[#D4AF37]">{totalGlobalAmount.toLocaleString()} FCFA</span>
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}